import { HttpEvent } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';

// Types
import { FailedCall, HasAcceptedTermsAndConditions, OfflineFile } from '../../../shared/types/manageCalls';
import { TrackingActionsParams } from '../../../shared/types/params';

// Services
import { LoggerService } from '../../services/logger/logger.service';
import { OfflineService } from '../../services/offline/offline.service';
import { WebServiceCaller } from '../../services/webservice-caller/webservice-caller.service';

@Injectable()
export class AppFunctionalitiesService {
  private wsCaller = inject(WebServiceCaller);
  private injector = inject(Injector);
  private logger = inject(LoggerService);

  public trackAction(
    trackInfo: TrackingActionsParams | TrackingActionsParams[],
    isFromCheckOfflineApis: boolean = false,
  ): Observable<void | FailedCall> {
    type Response = void | FailedCall;

    if (isFromCheckOfflineApis) {
      return this.wsCaller.postCall<Response>('trackingActions', trackInfo);
    } else {
      const payload = (typeof trackInfo === 'object' ? [trackInfo] : trackInfo) as TrackingActionsParams[];
      if ((trackInfo as TrackingActionsParams).timestampOffline) {
        return this.wsCaller.postCall<Response>('trackingActions', payload);
      } else {
        return this.wsCaller.postCall<Response>('trackingActions', payload).pipe(
          catchError((error: FailedCall) => {
            this.logger.consoleError('AppFunctionalitiesService', 'trackAction', { error });
            const offlineService = this.injector.get(OfflineService);

            if (error === null || error?.code === 'CORS_ERROR') {
              const params: TrackingActionsParams = { ...trackInfo, timestampOffline: Date.now() };
              offlineService.addApiToCallWhenOnline('trackAction', params);
            }

            return throwError(() => error);
          }),
        );
      }
    }
  }

  public acceptTermsAndConditions(): Observable<void | FailedCall> {
    return this.wsCaller.putCall('acceptTermsAndConditions', {});
  }

  public hasAcceptedTermsAndConditions(): Observable<HasAcceptedTermsAndConditions | FailedCall> {
    return this.wsCaller.postCall('hasAcceptedTermsAndConditions', {});
  }

  public getOfflineAudioFile(): Observable<OfflineFile | FailedCall> {
    return this.wsCaller.getCall('appFunctions/offlineAudioFile');
  }

  public getAudioFileBuffer(url: string): Observable<HttpEvent<ArrayBuffer> | FailedCall> {
    return this.wsCaller.postCallBuffer('appFunctions/audioFileBuffer', { url });
  }
}
